import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Rocket, MapPin, Users, Calendar } from "lucide-react";
import AppShell, { BrandMark } from "@/components/AppShell";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const INDUSTRY_LABELS = {
  hospitals: "Hospitals", pharma: "Pharma", diagnostics: "Diagnostics",
  medtech: "MedTech", biotech: "Biotech", digital_health: "Digital Health",
  insurance: "Insurance", health_ai: "Health AI", telemedicine: "Telemedicine",
  wellness: "Wellness", consulting: "Consulting",
};

const STAGE_LABEL = {
  seed: "Seed", series_a: "Series A", series_b: "Series B",
  series_c: "Series C", growth: "Growth", public: "Public",
};

export default function StartupProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/startups/${id}`);
      setS(data);
    })();
  }, [id]);

  if (!s) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  const isAuthed = !!user && user !== false;

  const content = (
    <div className={isAuthed ? "grid grid-cols-1 lg:grid-cols-3 gap-8" : "max-w-5xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8"}>
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-border bg-white p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-md bg-primary/8 text-primary flex items-center justify-center shrink-0">
                <Rocket size={22} />
              </div>
              <div>
                <div className="overline">{INDUSTRY_LABELS[s.industry] || s.industry} · {STAGE_LABEL[s.stage] || s.stage}</div>
                <h1 className="mt-1 font-serif text-3xl md:text-4xl text-primary" data-testid="startup-name">{s.name}</h1>
                <div className="mt-1 text-sm text-muted-foreground">{s.tagline}</div>
              </div>
            </div>
            <p className="mt-6 text-sm text-foreground leading-relaxed">{s.description}</p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Stat icon={Calendar} label="Founded" value={s.founded_year} />
              <Stat icon={MapPin} label="HQ" value={s.hq_city} />
              <Stat icon={Users} label="Team" value={s.team_size} />
              <Stat label="Funding" value={`₹${s.funding_raised_inr_cr}Cr`} />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-6 md:p-8">
            <div className="overline mb-3">Product lines</div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="startup-product-lines">
              {(s.product_lines || []).map((p) => (
                <li key={p} className="rounded-md border border-border bg-background p-3 text-sm text-primary font-medium">{p}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-primary text-primary-foreground p-6" data-testid="startup-founders">
            <div className="overline text-primary-foreground/70">Founders</div>
            <ul className="mt-3 space-y-2">
              {(s.founders || []).map((f) => (
                <li key={f} className="text-sm">{f}</li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-border bg-white p-6">
            <div className="overline mb-2">At a glance</div>
            <dl className="text-sm space-y-2">
              <Row k="Industry" v={INDUSTRY_LABELS[s.industry] || s.industry} />
              <Row k="Stage" v={STAGE_LABEL[s.stage] || s.stage} />
              <Row k="Founded" v={s.founded_year} />
              <Row k="Funding" v={`₹${s.funding_raised_inr_cr} Cr`} />
              <Row k="Team" v={s.team_size} />
              <Row k="HQ" v={s.hq_city} />
            </dl>
          </div>
        </div>
      </div>
  );

  if (isAuthed) {
    return (
      <AppShell
        pageTitle={s.name}
        pageDescription={s.tagline}
        actions={<Link to="/startups" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="back-to-startups"><ArrowLeft size={14} /> Back to Startups</Link>}
      >
        {content}
      </AppShell>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <Link to="/startups" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to Startups</Link>
        </div>
      </header>
      {content}
      <Footer />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon size={11} />} {label}
      </div>
      <div className="mt-1 text-primary font-semibold">{value}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 py-1.5 last:border-0">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-primary font-medium">{v}</dd>
    </div>
  );
}
